import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PageTitle from "../Components/PageTitle";
import AxiosApi from "../Helper/axios.api";
import helper from "../Helper/Helper";
import { Input } from "reactstrap";
import "../assets/css/edit.css";

// Upload icon - you might want to create or import this
const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function Edit() {
  const auth = useSelector((state) => state.auth);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [cacheTimestamp, setCacheTimestamp] = useState(Date.now()); // Add cache timestamp for invalidation
  const [isUploading, setIsUploading] = useState(false);
  const [processing, setProcessing] = useState({ enhance: false, extend: false, edit: false });
  const [results, setResults] = useState({ enhance: null, extend: null, edit: null });
  const [errors, setErrors] = useState({});
  const [extendOption, setExtendOption] = useState("best_decide");
  const [editPrompt, setEditPrompt] = useState("");
  const [currentEditId, setCurrentEditId] = useState(null); // Store edit ID when there's an upload but no results
  const fileInputRef = useRef(null);
  const pollIntervalsRef = useRef([]);
  // Carousel state for each result type
  const [carouselIndex, setCarouselIndex] = useState({ enhance: 0, extend: 0, edit: 0 });
  // Modal state for image preview
  const [modalState, setModalState] = useState({ 
    isOpen: false, 
    imageUrl: '', 
    imageName: '', 
    downloadFunction: null,
    editId: null 
  });

  // Helper function to generate random filename to avoid browser duplicates
  const generateRandomFilename = (baseName, extension = '.png') => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const nameWithoutExt = baseName.replace(/\.(png|jpg|jpeg)$/i, '');
    return `${nameWithoutExt}_${timestamp}_${randomStr}${extension}`;
  };

  // Helper function to generate cache-busted URL for server images
  const getCacheBustedUrl = (baseUrl, forceRefresh = false) => {
    if (!baseUrl) return baseUrl;
    
    // If it's a blob URL (from fresh uploads), don't modify it
    if (baseUrl.startsWith('blob:')) return baseUrl;
    
    // For server URLs, add cache busting parameter
    const separator = baseUrl.includes('?') ? '&' : '?';
    const timestamp = forceRefresh ? Date.now() : cacheTimestamp;
    return `${baseUrl}${separator}t=${timestamp}`;
  };

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollIntervalsRef.current.forEach(interval => clearInterval(interval));
    };
  }, []);

  // Load existing data when component mounts
  useEffect(() => {
    loadExistingData();
  }, []);

  // Modal functions
  const openImageModal = (imageUrl, imageName, downloadFunction, editId) => {
    setModalState({
      isOpen: true,
      imageUrl,
      imageName,
      downloadFunction,
      editId
    });
  };

  const closeImageModal = () => {
    setModalState({
      isOpen: false,
      imageUrl: '',
      imageName: '',
      downloadFunction: null,
      editId: null
    });
  };

  const useImageForEditing = async () => {
    try {
      setIsUploading(true);
      
      // Clear any ongoing polling
      pollIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollIntervalsRef.current = [];
      
      // Call backend to make the selected image the new upload and clear results
      const response = await AxiosApi.withToken(
        auth.accessToken,
        "edit/use-result-for-editing",
        "post",
        {
          user_id: auth.userDeatils.user_id,
          edit_id: modalState.editId,
          image_path: modalState.imageUrl
        }
      );

      if (response.status) {
        // Reset states
        setResults({ enhance: null, extend: null, edit: null });
        setErrors({});
        setProcessing({ enhance: false, extend: false, edit: false });
        setExtendOption("best_decide");
        setEditPrompt("");
        setCarouselIndex({ enhance: 0, extend: 0, edit: 0 });
        
        // Update the current edit ID to the one we're now working with
        setCurrentEditId(response.data.edit_id || modalState.editId);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Set the new upload state with cache busting to ensure fresh image load
        setUploadedImage(true);
        
        // Update the current edit ID to the one we're now working with
        setCurrentEditId(response.data.edit_id || modalState.editId);
        
        // Force cache refresh and set new preview URL
        setCacheTimestamp(Date.now());
        const newImageUrl = response.data.new_upload_path || `/download/edit/${modalState.editId}/upload.png`;
        const fullUrl = `${helper.ApiBaseUrl}${newImageUrl}`;
        setPreviewUrl(getCacheBustedUrl(fullUrl, true));
        
        toast.success("Image loaded for editing successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
        
        // Close modal
        closeImageModal();
      } else {
        throw new Error(response.message || "Failed to use image for editing");
      }
    } catch (error) {
      console.error('Error using image for editing:', error);
      toast.error("Failed to load image for editing", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Image Carousel Component
  const ImageCarousel = ({ images, type, downloadFunction, downloadAllFunction, title }) => {
    const maxVisible = 4;
    const currentIndex = carouselIndex[type];
    const totalImages = images.length;
    const visibleImages = images.slice(currentIndex, currentIndex + maxVisible);
    const canNavigateLeft = currentIndex > 0;
    const canNavigateRight = currentIndex + maxVisible < totalImages;
    const shouldShowArrows = totalImages > maxVisible;

    // Get the edit ID from results or stored value
    const getEditId = () => {
      if (type === 'enhance' && results.enhance?.id) return results.enhance.id;
      if (type === 'extend' && results.extend?.id) return results.extend.id;
      if (type === 'edit' && results.edit?.id) return results.edit.id;
      // If no results but we have an uploaded image, return the stored edit ID
      return currentEditId;
    };

    const navigateLeft = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (canNavigateLeft) {
        setCarouselIndex(prev => ({
          ...prev,
          [type]: Math.max(0, prev[type] - maxVisible)
        }));
      }
    };

    const navigateRight = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (canNavigateRight) {
        setCarouselIndex(prev => ({
          ...prev,
          [type]: Math.min(totalImages - maxVisible, prev[type] + maxVisible)
        }));
      }
    };

    return (
      <div className="result-card h-100 d-flex flex-column" style={{
        border: '1px solid #e3e6ea',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <div className="result-header p-3 bg-primary text-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0">{title}</h6>
          <button 
            className="btn btn-outline-light btn-sm"
            onClick={() => downloadAllFunction()}
            title={`Download all ${type} images (${totalImages} ${totalImages === 1 ? 'image' : 'images'})`}
          >
            <i className="fa fa-download me-1"></i>
            Download All
          </button>
        </div>
        <div className="result-content p-3 flex-grow-1 d-flex flex-column">
          {images && images.length > 0 ? (
            <>
              <div 
                className="position-relative carousel-container"
                style={{ minHeight: '200px' }}
              >
                <div 
                  className="mb-3 d-flex flex-wrap" 
                  style={{ gap: '0.5rem' }}
                >
                  {visibleImages.map((imageUrl, index) => {
                    const actualIndex = currentIndex + index;
                    return (
                      <div 
                        key={actualIndex}
                        className="position-relative image-item"
                        style={{
                          width: 'calc(50% - 0.25rem)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          const downloadBtn = e.currentTarget.querySelector('.individual-download-btn');
                          const img = e.currentTarget.querySelector('img');
                          const overlay = e.currentTarget.querySelector('.click-overlay');
                          if (downloadBtn) downloadBtn.style.opacity = '1';
                          if (img) {
                            img.style.transform = 'scale(1.02)';
                            img.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                          }
                          if (overlay) overlay.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const downloadBtn = e.currentTarget.querySelector('.individual-download-btn');
                          const img = e.currentTarget.querySelector('img');
                          const overlay = e.currentTarget.querySelector('.click-overlay');
                          if (downloadBtn) downloadBtn.style.opacity = '0';
                          if (img) {
                            img.style.transform = 'scale(1)';
                            img.style.boxShadow = 'none';
                          }
                          if (overlay) overlay.style.opacity = '0';
                        }}
                        onClick={() => {
                          const baseImageName = `${title.toLowerCase().replace(/\s+/g, '_')}_${actualIndex + 1}`;
                          const imageName = generateRandomFilename(baseImageName);
                          openImageModal(
                            imageUrl, 
                            imageName, 
                            () => downloadFunction(imageUrl, actualIndex),
                            getEditId()
                          );
                        }}
                      >
                        <img 
                          src={getCacheBustedUrl(`${helper.ApiBaseUrl}${imageUrl}`)}
                          alt={`${title} ${actualIndex + 1}`}
                          style={{
                            width: '100%', 
                            height: 'auto', 
                            borderRadius: '8px',
                            display: 'block',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                          }}
                        />
                        
                        {/* Click overlay */}
                        <div 
                          className="click-overlay position-absolute"
                          style={{
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: '0',
                            transition: 'opacity 0.2s ease',
                            pointerEvents: 'none'
                          }}
                        >
                          <div 
                            style={{
                              color: '#fff',
                              fontSize: '1.2rem',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              textShadow: '0 1px 3px rgba(0,0,0,0.7)'
                            }}
                          >
                            <i className="fa fa-search-plus mb-1"></i>
                            <div style={{ fontSize: '0.8rem' }}>Click to view</div>
                          </div>
                        </div>
                        <button
                          className="individual-download-btn btn btn-sm btn-primary position-absolute"
                          style={{
                            top: '5px',
                            right: '5px',
                            opacity: '0',
                            transition: 'opacity 0.2s ease',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadFunction(imageUrl, actualIndex);
                          }}
                          title={`Download ${title} ${actualIndex + 1}`}
                        >
                          <i className="fa fa-download"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
                
                {/* Navigation Arrows */}
                {shouldShowArrows && canNavigateLeft && (
                  <button
                    className="carousel-nav-btn btn btn-primary position-absolute"
                    style={{
                      left: '-15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: '0.7',
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      zIndex: '10',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.7';
                    }}
                    onClick={navigateLeft}
                    title="Previous images"
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                )}
                
                {shouldShowArrows && canNavigateRight && (
                  <button
                    className="carousel-nav-btn btn btn-primary position-absolute"
                    style={{
                      right: '-15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: '0.7',
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      zIndex: '10',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.7';
                    }}
                    onClick={navigateRight}
                    title="Next images"
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                )}
                
                {/* Image Counter */}
                {totalImages > maxVisible && (
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      Showing {currentIndex + 1}-{Math.min(currentIndex + maxVisible, totalImages)} of {totalImages} images
                    </small>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-muted d-flex flex-column justify-content-center flex-grow-1">
              <i className="fa fa-exclamation-circle mb-2" style={{fontSize: '2rem', color: '#6c757d'}}></i>
              <p className="mb-1">No results available</p>
              <small>The {type} may have failed or not produced any results</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const loadExistingData = async () => {
    try {
      // Get edit history to check for existing uploads and results
      const response = await AxiosApi.withToken(
        auth.accessToken,
        `edit/history?user_id=${auth.userDeatils.user_id}&per_page=10`,
        "get"
      );

      if (response.status && response.data) {
        let hasOriginalImage = false;
        const newResults = { enhance: null, extend: null, edit: null };

        // Check if there are edit entries with results
        if (response.data.edits && response.data.edits.length > 0) {
          // Process all edit entries (one for each category)
          response.data.edits.forEach(editEntry => {
            // Load original image (same for all categories)
            if (editEntry.original_image_url && !hasOriginalImage) {
              // Use cache-busted URL for server images
              const fullUrl = `${helper.ApiBaseUrl}${editEntry.original_image_url}`;
              setPreviewUrl(getCacheBustedUrl(fullUrl, true));
              setUploadedImage(true);
              hasOriginalImage = true;
            }

            // Load results for each category
            if (editEntry.result_images_urls && editEntry.result_images_urls.length > 0) {
              const editType = editEntry.edit_type;
              newResults[editType] = {
                id: editEntry.id,
                edit_type: editEntry.edit_type,
                image_urls: editEntry.result_images_urls,
                // Keep compatibility with existing code
                image_url: editEntry.result_images_urls[0]
              };
              // Store the edit ID (will be used when no specific result type is available)
              setCurrentEditId(editEntry.id);
            }
          });
        }

        // Check for uploaded image even if no results exist
        if (!hasOriginalImage && response.data.has_uploaded_image && response.data.original_image_url) {
          // Use cache-busted URL for server images
          const fullUrl = `${helper.ApiBaseUrl}${response.data.original_image_url}`;
          setPreviewUrl(getCacheBustedUrl(fullUrl, true));
          setUploadedImage(true);
          setCurrentEditId(response.data.edit_id); // Store the edit ID for future operations
        }

        // Update results state with all categories
        setResults(newResults);

        // Check for ongoing processes by looking at database records
        await checkOngoingProcesses();
      }
    } catch (error) {
      console.error('Failed to load existing data:', error);
      // Don't show error to user as this is just for convenience
    }
  };

  const checkOngoingProcesses = async () => {
    try {
      // Check if there are any active EditImage records in the database
      const statusResponse = await AxiosApi.withToken(
        auth.accessToken,
        `edit/status?user_id=${auth.userDeatils.user_id}`,
        "get"
      );

      if (statusResponse.status && statusResponse.data && statusResponse.data.active_processes) {
        const activeProcesses = statusResponse.data.active_processes;
        const newProcessing = { enhance: false, extend: false, edit: false };

        // Set processing state for active processes
        activeProcesses.forEach(process => {
          if (process.edit_type === 'enhance') {
            newProcessing.enhance = true;
            startPolling('enhance');
          } else if (process.edit_type === 'extend') {
            newProcessing.extend = true;
            startPolling('extend');
          } else if (process.edit_type === 'edit') {
            newProcessing.edit = true;
            startPolling('edit');
          }
        });

        setProcessing(newProcessing);

        if (activeProcesses.length > 0) {
          toast.info(`Resuming ${activeProcesses.length} ongoing AI process${activeProcesses.length > 1 ? 'es' : ''}...`, {
            position: "top-center",
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.error('Failed to check ongoing processes:', error);
      // Don't show error to user as this is optional functionality
    }
  };

  const startPolling = (action) => {
    // For resumed processes, we need to find the edit_id from the database
    // For now, use a placeholder approach - poll by checking the edit/status endpoint
    const pollWithStatus = async () => {
      try {
        const statusResponse = await AxiosApi.withToken(
          auth.accessToken,
          `edit/status?user_id=${auth.userDeatils.user_id}`,
          "get"
        );

        if (statusResponse.status && statusResponse.data && statusResponse.data.active_processes) {
          const activeProcess = statusResponse.data.active_processes.find(p => p.edit_type === action);
          if (activeProcess) {
            // Found active process, continue with normal polling
            pollForCompletion(action, activeProcess.id);
          } else {
            // No active process found, stop processing state
            setProcessing(prev => ({ ...prev, [action]: false }));
          }
        } else {
          // No active processes, stop processing state
          setProcessing(prev => ({ ...prev, [action]: false }));
        }
      } catch (error) {
        console.error('Error checking process status:', error);
        setProcessing(prev => ({ ...prev, [action]: false }));
      }
    };

    pollWithStatus();
  };

  const clearAllImages = async (keepRecord = false) => {
    try {
      // Call API to clear all user images
      await AxiosApi.withToken(
        auth.accessToken,
        "edit/clear",
        "post",
        { 
          user_id: auth.userDeatils.user_id,
          keep_record: keepRecord 
        }
      );
    } catch (error) {
      console.error('Failed to clear images:', error);
      // Don't show error to user as the UI cleanup will happen regardless
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ upload: "File type not supported. Please upload a JPG or PNG image." });
      return;
    }

    if (file.size > 10000000) { // 10MB limit
      setErrors({ upload: "File size limit is 10MB" });
      return;
    }

    setErrors({});
    setIsUploading(true);

    // Clear results state immediately to prevent stale data
    setResults({ enhance: null, extend: null, edit: null });
    setCurrentEditId(null);

    try {
      // Create FormData for upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", auth.userDeatils.user_id);
      formData.append("filename", "upload.png"); // Using the specified filename

      // Upload the image
      const response = await AxiosApi.withToken(
        auth.accessToken,
        "edit/upload",
        "post",
        formData
      );

      if (response.status) {
        setUploadedImage(file);
        
        // Store the edit ID for future operations
        if (response.data && response.data.edit_id) {
          setCurrentEditId(response.data.edit_id);
        }
        
        // Update cache timestamp to ensure fresh loads
        setCacheTimestamp(Date.now());
        
        // Use blob URL for immediate preview to avoid any caching issues
        setPreviewUrl(URL.createObjectURL(file));
        
        toast.success("Image uploaded successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        setErrors({ upload: response.message || "Upload failed" });
      }
    } catch (error) {
      setErrors({ upload: "Upload failed. Please try again." });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      // Simulate file input change
      const event = { target: { files: [files[0]] } };
      handleFileSelect(event);
    }
  };

  const handleAIAction = async (action) => {
    if (!uploadedImage) {
      toast.error("Please upload an image first", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    // Validate edit prompt if action is edit
    if (action === 'edit' && !editPrompt.trim()) {
      toast.error("Please provide a description of how you want to edit the image", {
        position: "top-center",
        autoClose: 3000,
      });
      setErrors(prev => ({ ...prev, edit: "Please provide a description of how you want to edit the image" }));
      return;
    }

    setProcessing(prev => ({ ...prev, [action]: true }));
    setErrors({});

    try {
      const data = {
        user_id: auth.userDeatils.user_id,
        action: action,
        image_path: "upload.png" // Using the specified path
      };

      if (action === 'extend') {
        data.extend_option = extendOption;
      }

      if (action === 'edit') {
        data.edit_prompt = editPrompt.trim();
      }

      const response = await AxiosApi.withToken(
        auth.accessToken,
        `edit/${action}`,
        "post",
        data
      );

      const responseData = response?.data || {};
      const editIdFromResponse = responseData.edit_id || response?.edit_id || currentEditId;

      if (!editIdFromResponse) {
        throw new Error(response?.message || `${action} request did not return an edit id.`);
      }

      setCurrentEditId(editIdFromResponse);

      toast.info(`${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} processing started...`, {
        position: "top-center",
        autoClose: 3000,
      });

      pollForCompletion(action, editIdFromResponse);

      if (response && response.status === false && response.message) {
        console.warn(`Edit ${action} queued with warning: ${response.message}`);
      }
    } catch (error) {
      const fallbackMessage = `${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} failed. Please try again.`;
      const errorMessage = error?.message || fallbackMessage;
      setErrors(prev => ({ ...prev, [action]: errorMessage }));
      setProcessing(prev => ({ ...prev, [action]: false }));
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    }
  };

  const pollForCompletion = async (action, editId) => {
    if (!editId) {
      console.error('No edit ID provided for polling');
      setProcessing(prev => ({ ...prev, [action]: false }));
      const errorMsg = `${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} failed to start properly.`;
      setErrors(prev => ({ ...prev, [action]: errorMsg }));
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AxiosApi.withToken(
          auth.accessToken,
          `edit/${editId}/status`,
          "get"
        );

        if (response.status && response.data) {
          const { process_state, result_images_urls } = response.data;
          
          if (process_state === 'DO') { // DONE
            clearInterval(pollInterval);
            pollIntervalsRef.current = pollIntervalsRef.current.filter(id => id !== pollInterval);
            setProcessing(prev => ({ ...prev, [action]: false }));
            
            // Update cache timestamp to ensure fresh image loads for new results
            setCacheTimestamp(Date.now());
            
            setResults(prev => ({ 
              ...prev, 
              [action]: { 
                ...response.data,
                image_urls: result_images_urls || [],
                image_url: result_images_urls && result_images_urls.length > 0 ? result_images_urls[0] : null
              }
            }));
            toast.success(`${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} completed successfully!`, {
              position: "top-center",
              autoClose: 3000,
            });
          } else if (process_state === 'FA') { // FAILED
            clearInterval(pollInterval);
            pollIntervalsRef.current = pollIntervalsRef.current.filter(id => id !== pollInterval);
            setProcessing(prev => ({ ...prev, [action]: false }));
            const errorMsg = `${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} failed during processing.`;
            setErrors(prev => ({ ...prev, [action]: errorMsg }));
            toast.error(errorMsg, {
              position: "top-center",
              autoClose: 5000,
            });
          }
          // If still 'RU' (RUNNING), continue polling
        }
      } catch (error) {
        console.error('Polling error:', error);
        
        // If we get a 404 or other error that suggests the edit doesn't exist,
        // stop polling and show an error
        if (error.response?.status === 404) {
          clearInterval(pollInterval);
          pollIntervalsRef.current = pollIntervalsRef.current.filter(id => id !== pollInterval);
          setProcessing(prev => ({ ...prev, [action]: false }));
          const errorMsg = `${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} status not found.`;
          setErrors(prev => ({ ...prev, [action]: errorMsg }));
          toast.error(errorMsg, {
            position: "top-center",
            autoClose: 5000,
          });
        }
        // Continue polling on other errors - might be temporary network issues
      }
    }, 3000); // Poll every 3 seconds

    // Track the interval
    pollIntervalsRef.current.push(pollInterval);

    // Set a timeout to stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      pollIntervalsRef.current = pollIntervalsRef.current.filter(id => id !== pollInterval);
      if (processing[action]) {
        setProcessing(prev => ({ ...prev, [action]: false }));
        toast.warning(`${action.charAt(0).toUpperCase() + action.slice(1).replace(/([A-Z])/g, ' $1')} is taking longer than expected. Please check back later.`, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    }, 300000); // 5 minutes timeout
  };

  const resetAll = async () => {
    // Clear any ongoing polling
    pollIntervalsRef.current.forEach(interval => clearInterval(interval));
    pollIntervalsRef.current = [];
    
    // Clear all images from server but keep the record for consistency
    await clearAllImages(true);
    
    setUploadedImage(null);
    setPreviewUrl("");
    setCacheTimestamp(Date.now()); // Reset cache timestamp
    setResults({ enhance: null, extend: null, edit: null });
    setCurrentEditId(null);
    setErrors({});
    setProcessing({ enhance: false, extend: false, edit: false });
    setExtendOption("best_decide");
    setEditPrompt("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadImage = async (result, baseFilename) => {
    try {
      if (result && result.image_urls && result.image_urls.length > 0) {
        // Get the actual edit_id from the result data
        const editId = result.id;
        
        if (!editId) {
          throw new Error('Edit ID not found in result data');
        }
        
        // If multiple images, download each one
        if (result.image_urls.length > 1) {
          let successCount = 0;
          for (let i = 0; i < result.image_urls.length; i++) {
            const imageUrl = result.image_urls[i];
            const baseNameWithoutExt = baseFilename.replace(/\.(png|jpg|jpeg)$/i, '');
            const filename = generateRandomFilename(`${baseNameWithoutExt}_${i + 1}`);
            const success = await downloadIndividualImage(imageUrl, filename, editId);
            if (success) successCount++;
            
            // Small delay between downloads to avoid overwhelming the server
            if (i < result.image_urls.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
          
          if (successCount > 0) {
            toast.success(`Downloaded ${successCount} of ${result.image_urls.length} images`, {
              position: "top-center",
              autoClose: 3000,
            });
          }
        } else {
          // Single image download
          const success = await downloadIndividualImage(result.image_urls[0], baseFilename, editId);
          if (success) {
            toast.success(`Downloaded ${baseFilename}`, {
              position: "top-center",
              autoClose: 2000,
            });
          }
        }
      } else if (result && result.image_url && result.id) {
        // Fallback for old format - use backend proxy endpoint
        const url = `${helper.ApiUrl}edit/download?edit_id=${encodeURIComponent(result.id)}&type=result&filename=${encodeURIComponent(baseFilename)}`;

        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': auth.accessToken,
          },
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error(`Download failed: ${res.status}`);
        }

        const blob = await res.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = baseFilename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);

        toast.success(`Downloaded ${baseFilename}`, {
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        toast.error("No image available for download", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to download image", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const downloadIndividualImage = async (imageUrl, filename, editId) => {
    try {
      // Use the actual edit_id from the result data
      const url = `${helper.ApiUrl}edit/download?edit_id=${encodeURIComponent(editId)}&type=result&filename=${encodeURIComponent(filename)}&image_path=${encodeURIComponent(imageUrl)}`;

      // Fetch as blob with auth header
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': auth.accessToken,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error(`Download failed: ${res.status}`);
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      return true; // Return success indicator for bulk downloads
    } catch (error) {
      console.error('Individual download error:', error);
      toast.error(`Failed to download ${filename}`, {
        position: "top-center",
        autoClose: 3000,
      });
      return false; // Return failure indicator
    }
  };

  const downloadPreviewImage = async () => {
    try {
      if (!currentEditId) {
        toast.error("No image available for download", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      const filename = generateRandomFilename('preview_image');
      const url = `${helper.ApiUrl}edit/download?edit_id=${encodeURIComponent(currentEditId)}&type=original&filename=${encodeURIComponent(filename)}`;

      // Fetch as blob with auth header
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': auth.accessToken,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error(`Download failed: ${res.status}`);
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      toast.success("Preview image downloaded successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Preview download error:', error);
      toast.error("Failed to download preview image", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <PageTitle title="AI Image Editor" />
      <div className="container-fluid p-4">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mb-0 d-flex align-items-center">
                    <i className="fa fa-edit me-2" style={{color: '#65badbff'}}></i>
                    AI Image Editor
                  </h2>
                  {uploadedImage && (
                    <button 
                      className="btn btn-outline-secondary btn-sm"
                      onClick={resetAll}
                    >
                      <i className="fa fa-refresh me-1"></i>
                      Start Over
                    </button>
                  )}
                </div>

                {/* Upload Section */}
                <div className="row">
                  <div className="col-lg-6">
                    <div className="upload-section mb-4">
                      <label className="mb-3 d-flex align-items-center" style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#495057'
                      }}>
                        <i className="fa fa-upload me-2" style={{color: '#65badbff', fontSize: '14px'}}></i>
                        Upload Image
                      </label>
                      
                      <div 
                        className={`upload-area ${uploadedImage ? 'uploaded' : ''}`}
                        style={{
                          border: errors.upload ? '2px dashed #dc3545' : '2px dashed #ced4da',
                          borderRadius: '12px',
                          padding: '2rem',
                          textAlign: 'center',
                          backgroundColor: '#f8f9fa',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          height: '300px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="d-none"
                          accept="image/png,image/jpg,image/jpeg"
                          onChange={handleFileSelect}
                        />
                        
                        {isUploading ? (
                          <div className="d-flex flex-column align-items-center">
                            <div className="spinner-border text-primary mb-2" role="status">
                              <span className="visually-hidden">Uploading...</span>
                            </div>
                            <span className="text-muted">Uploading...</span>
                          </div>
                        ) : uploadedImage ? (
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa fa-check-circle text-success mb-2" style={{fontSize: '2rem'}}></i>
                            <span className="text-success fw-bold">Image uploaded successfully!</span>
                            <span className="text-muted small mt-1">Click to upload a different image</span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div className="upload-icon mb-3" style={{color: '#65badbff'}}>
                              <UploadIcon />
                            </div>
                            <span className="fw-bold mb-2">Choose an image or drag it here</span>
                            <span className="text-muted small">
                              Supported formats: JPG, PNG (max 10MB)
                            </span>
                          </div>
                        )}
                      </div>

                      {errors.upload && (
                        <div className="alert alert-danger mt-2">
                          <i className="fa fa-exclamation-triangle me-2"></i>
                          {errors.upload}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preview Section */}
                  {previewUrl && (
                    <div className="col-lg-6">
                      <label className="mb-3 d-flex align-items-center" style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#495057'
                      }}>
                        <i className="fa fa-image me-2" style={{color: '#65badbff', fontSize: '14px'}}></i>
                        Preview
                      </label>
                      <div className="preview-container position-relative" style={{
                        border: '1px solid #ced4da',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        const downloadBtn = e.currentTarget.querySelector('.preview-download-btn');
                        if (downloadBtn) downloadBtn.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        const downloadBtn = e.currentTarget.querySelector('.preview-download-btn');
                        if (downloadBtn) downloadBtn.style.opacity = '0';
                      }}
                      >
                        <img 
                          src={getCacheBustedUrl(previewUrl)} 
                          alt="Preview" 
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '300px',
                            objectFit: 'contain'
                          }}
                        />
                        <button
                          className="preview-download-btn btn btn-sm btn-primary position-absolute"
                          style={{
                            top: '10px',
                            right: '10px',
                            opacity: '0',
                            transition: 'opacity 0.2s ease',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPreviewImage();
                          }}
                          title="Download preview image"
                        >
                          <i className="fa fa-download"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Actions Section */}
                {uploadedImage && (
                  <div className="ai-actions-section mt-5">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h3 className="mb-0 d-flex align-items-center">
                        <i className="fa fa-magic me-2" style={{color: '#65badbff'}}></i>
                        AI Enhancement Options
                      </h3>
                      {Object.values(processing).some(p => p) && (
                        <div className="processing-indicator d-flex align-items-center text-primary">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Processing...</span>
                          </div>
                          <span className="fw-bold">
                            {processing.enhance ? 'Enhancing Face...' :
                             processing.extend ? 'Extending View...' :
                             processing.edit ? 'Editing Image...' : 'Processing...'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="row g-4">
                      {/* Enhance Face */}
                      <div className="col-lg-4">
                        <div className="action-card h-100 d-flex flex-column" style={{
                          border: '1px solid #e3e6ea',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div className="d-flex align-items-center mb-3">
                            <div className="action-icon me-3" style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              backgroundColor: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <i className="fa fa-user" style={{color: '#1976d2', fontSize: '20px'}}></i>
                            </div>
                            <h5 className="mb-0 fw-bold">Enhance Face</h5>
                          </div>
                          <p className="text-muted mb-3">
                            Improve facial features, reduce noise, and enhance overall face quality using advanced AI algorithms.
                            <br /><small className="text-muted" style={{fontSize: '0.85em'}}>Expected processing time: ~10 seconds</small>
                          </p>
                          
                          {/* Enhancement Features */}
                          <div className="mb-3">
                            <div className="enhancement-features" style={{
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              padding: '1rem',
                              fontSize: '0.9rem'
                            }}>
                              <div className="fw-bold mb-2 text-primary">Enhancement includes:</div>
                              <ul className="mb-0 ps-3" style={{lineHeight: '1.4'}}>
                                <li>Sharpen facial details</li>
                                <li>Reduce image noise</li>
                                <li>Improve skin texture</li>
                                <li>Enhance eye clarity</li>
                                <li>Balance facial lighting</li>
                              </ul>
                            </div>
                          </div>

                          <div className="mt-auto">
                            <button 
                              className="btn btn-primary w-100"
                              onClick={() => handleAIAction('enhance')}
                              disabled={Object.values(processing).some(p => p) || isUploading || results.enhance}
                              title={
                                isUploading ? "Please wait for upload to complete" :
                                results.enhance ? "Face enhancement already completed" :
                                processing.enhance ? "Enhancement in progress..." :
                                processing.extend ? "Please wait for Extend View to complete" :
                                processing.edit ? "Please wait for Edit Image to complete" :
                                "Enhance facial features and quality"
                              }
                            >
                              {processing.enhance ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Processing...</span>
                                  </div>
                                  Processing...
                                </>
                              ) : results.enhance ? (
                                <>
                                  <i className="fa fa-check me-2"></i>
                                  Already Enhanced
                                </>
                              ) : Object.values(processing).some(p => p) ? (
                                <>
                                  <i className="fa fa-clock me-2"></i>
                                  Waiting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-magic me-2"></i>
                                  Enhance Face
                                </>
                              )}
                            </button>
                            {errors.enhance && (
                              <div className="text-danger small mt-2">
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                {errors.enhance}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Extend View */}
                      <div className="col-lg-4">
                        <div className="action-card h-100 d-flex flex-column" style={{
                          border: '1px solid #e3e6ea',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div className="d-flex align-items-center mb-3">
                            <div className="action-icon me-3" style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              backgroundColor: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <i className="fa fa-expand" style={{color: '#1976d2', fontSize: '20px'}}></i>
                            </div>
                            <h5 className="mb-0 fw-bold">Extend View</h5>
                          </div>
                          <p className="text-muted mb-3">
                            Intelligently expand the image beyond its current boundaries using AI-powered outpainting.
                            <br /><small className="text-muted" style={{fontSize: '0.85em'}}>Expected processing time: ~30 seconds</small>
                          </p>
                          
                          {/* Extend Options */}
                          <div className="mb-3">
                            <label className="form-label fw-bold mb-2">Choose extension context:</label>
                            <div className="d-flex flex-column" style={{gap: '0.5rem'}}>
                              <div className="form-check">
                                <input 
                                  className="form-check-input" 
                                  type="radio" 
                                  name="extendOption" 
                                  id="sitting_chair" 
                                  value="sitting_chair"
                                  checked={extendOption === 'sitting_chair'}
                                  onChange={(e) => setExtendOption(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="sitting_chair">
                                  Sitting in chair
                                </label>
                              </div>
                              <div className="form-check">
                                <input 
                                  className="form-check-input" 
                                  type="radio" 
                                  name="extendOption" 
                                  id="standing" 
                                  value="standing"
                                  checked={extendOption === 'standing'}
                                  onChange={(e) => setExtendOption(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="standing">
                                  Standing
                                </label>
                              </div>
                              <div className="form-check">
                                <input 
                                  className="form-check-input" 
                                  type="radio" 
                                  name="extendOption" 
                                  id="sitting_desk" 
                                  value="sitting_desk"
                                  checked={extendOption === 'sitting_desk'}
                                  onChange={(e) => setExtendOption(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="sitting_desk">
                                  Sitting behind desk
                                </label>
                              </div>
                              <div className="form-check">
                                <input 
                                  className="form-check-input" 
                                  type="radio" 
                                  name="extendOption" 
                                  id="best_decide" 
                                  value="best_decide"
                                  checked={extendOption === 'best_decide'}
                                  onChange={(e) => setExtendOption(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="best_decide">
                                  Let model decide best context
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="mt-auto">
                            <button 
                              className="btn btn-primary w-100"
                              onClick={() => handleAIAction('extend')}
                              disabled={Object.values(processing).some(p => p) || isUploading}
                              title={
                                isUploading ? "Please wait for upload to complete" :
                                processing.extend ? "View extension in progress..." :
                                processing.enhance ? "Please wait for Enhance Face to complete" :
                                processing.edit ? "Please wait for Edit Image to complete" :
                                "Expand image boundaries using AI"
                              }
                            >
                              {processing.extend ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Processing...</span>
                                  </div>
                                  Processing...
                                </>
                              ) : Object.values(processing).some(p => p) ? (
                                <>
                                  <i className="fa fa-clock me-2"></i>
                                  Waiting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-expand-arrows-alt me-2"></i>
                                  Extend View
                                </>
                              )}
                            </button>
                            {errors.extend && (
                              <div className="text-danger small mt-2">
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                {errors.extend}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Edit Image */}
                      <div className="col-lg-4">
                        <div className="action-card h-100 d-flex flex-column" style={{
                          border: '1px solid #e3e6ea',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div className="d-flex align-items-center mb-3">
                            <div className="action-icon me-3" style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              backgroundColor: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <i className="fa fa-paint-brush" style={{color: '#1976d2', fontSize: '20px'}}></i>
                            </div>
                            <h5 className="mb-0 fw-bold">Edit Image</h5>
                          </div>
                          <p className="text-muted mb-3">
                            Apply intelligent editing and modifications to your image using state-of-the-art AI image processing.
                            <br /><small className="text-muted" style={{fontSize: '0.85em'}}>Expected processing time: ~5 minutes</small>
                          </p>
                          
                          {/* Edit Prompt */}
                          <div className="mb-3">
                            <label className="form-label fw-bold mb-2">
                              Describe how to edit the image: <span className="text-danger">*</span>
                            </label>
                            <textarea
                              className={`form-control ${errors.edit && errors.edit.includes('description') ? 'is-invalid' : ''}`}
                              rows="3"
                              maxLength="500"
                              placeholder="e.g., Change the background to a beach scene, add sunglasses, remove the hat, make the lighting warmer..."
                              value={editPrompt}
                              onChange={(e) => {
                                setEditPrompt(e.target.value);
                                // Clear error when user starts typing
                                if (errors.edit && errors.edit.includes('description')) {
                                  setErrors(prev => ({ ...prev, edit: null }));
                                }
                              }}
                              disabled={Object.values(processing).some(p => p) || isUploading}
                              style={{
                                fontSize: '0.9rem',
                                resize: 'vertical',
                                minHeight: '80px'
                              }}
                            />
                            <div className="form-text d-flex justify-content-between">
                              <span>Be specific about what changes you want to make to get better results.</span>
                              <span className={editPrompt.length > 450 ? 'text-warning' : 'text-muted'}>
                                {editPrompt.length}/500 characters
                              </span>
                            </div>
                          </div>

                          <div className="mt-auto">
                            <button 
                              className="btn btn-primary w-100"
                              onClick={() => handleAIAction('edit')}
                              disabled={Object.values(processing).some(p => p) || isUploading || !editPrompt.trim()}
                              title={
                                isUploading ? "Please wait for upload to complete" :
                                !editPrompt.trim() ? "Please provide a description of how to edit the image" :
                                processing.edit ? "Image editing in progress..." :
                                processing.enhance ? "Please wait for Enhance Face to complete" :
                                processing.extend ? "Please wait for Extend View to complete" :
                                "Apply AI-powered image modifications"
                              }
                            >
                              {processing.edit ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Processing...</span>
                                  </div>
                                  Processing...
                                </>
                              ) : Object.values(processing).some(p => p) ? (
                                <>
                                  <i className="fa fa-clock me-2"></i>
                                  Waiting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-edit me-2"></i>
                                  Edit Image
                                </>
                              )}
                            </button>
                            {errors.edit && (
                              <div className="text-danger small mt-2">
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                {errors.edit}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Results Section */}
                {(results.enhance || results.extend || results.edit) && (
                  <div className="results-section mt-5">
                    <h3 className="mb-4 d-flex align-items-center">
                      <i className="fa fa-download me-2" style={{color: '#65badbff'}}></i>
                      Results
                    </h3>
                    <div className="row g-4">
                      {results.enhance && (
                        <div className="col-lg-4">
                          <ImageCarousel
                            images={results.enhance.image_urls}
                            type="enhance"
                            downloadFunction={(imageUrl, index) => 
                              downloadIndividualImage(imageUrl, generateRandomFilename(`enhanced_face_${index + 1}`), results.enhance?.id)
                            }
                            downloadAllFunction={() => downloadImage(results.enhance, generateRandomFilename('enhanced_face'))}
                            title="Enhanced Face"
                          />
                        </div>
                      )}
                      
                      {results.extend && (
                        <div className="col-lg-4">
                          <ImageCarousel
                            images={results.extend.image_urls}
                            type="extend"
                            downloadFunction={(imageUrl, index) => 
                              downloadIndividualImage(imageUrl, generateRandomFilename(`extended_view_${index + 1}`), results.extend?.id)
                            }
                            downloadAllFunction={() => downloadImage(results.extend, generateRandomFilename('extended_view'))}
                            title="Extended View"
                          />
                        </div>
                      )}
                      
                      {results.edit && (
                        <div className="col-lg-4">
                          <ImageCarousel
                            images={results.edit.image_urls}
                            type="edit"
                            downloadFunction={(imageUrl, index) => 
                              downloadIndividualImage(imageUrl, generateRandomFilename(`edited_image_${index + 1}`), results.edit?.id)
                            }
                            downloadAllFunction={() => downloadImage(results.edit, generateRandomFilename('edited_image'))}
                            title="Edited Image"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {modalState.isOpen && (
        <div 
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 1050,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={closeImageModal}
        >
          <div 
            className="modal-content"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '1.5rem',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="btn-close position-absolute"
              style={{
                top: '0px',
                right: '10px',
                fontSize: '2.2rem',
                border: 'none',
                background: 'none',
                color: '#666',
                cursor: 'pointer'
              }}
              onClick={closeImageModal}
              title="Close"
            >
              ×
            </button>

            {/* Image */}
            <div 
              style={{
                maxWidth: '80vw',
                maxHeight: '70vh',
                overflow: 'hidden',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}
            >
              <img 
                src={`${helper.ApiBaseUrl}${modalState.imageUrl}`}
                alt={modalState.imageName}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            </div>

            {/* Action buttons */}
            <div className="d-flex gap-3">
              <button
                className="btn btn-primary"
                onClick={() => {
                  modalState.downloadFunction();
                  closeImageModal();
                }}
                title="Download this image"
                disabled={isUploading}
              >
                <i className="fa fa-download me-2"></i>
                Download Image
              </button>
              <button
                className="btn btn-success"
                onClick={useImageForEditing}
                title="Use this image for further editing"
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fa fa-edit me-2"></i>
                    Use for Editing
                  </>
                )}
              </button>
            </div>

            <div className="text-center mt-2">
              <small className="text-muted">{modalState.imageName}</small>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
